import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Controlled `activeKey` state for antd `Collapse`.
 *
 * `defaultActiveKey` only applies on mount, so every panel that appeared after a tab or page
 * change stayed collapsed. Controlling the keys instead auto-expands newly arrived groups while
 * keeping panels the dealer collapsed by hand closed.
 */
export function useAutoExpandedKeys(keys: string[]): [string[], (key: string | string[]) => void] {
  const [activeKeys, setActiveKeys] = useState<string[]>(keys)
  const knownKeysRef = useRef(new Set(keys))

  useEffect(() => {
    const newKeys = keys.filter((key) => !knownKeysRef.current.has(key))
    if (newKeys.length === 0) return
    for (const key of newKeys) knownKeysRef.current.add(key)
    setActiveKeys((previous) => [...previous, ...newKeys])
  }, [keys])

  const onChange = useCallback((key: string | string[]) => {
    setActiveKeys(Array.isArray(key) ? key : [key])
  }, [])

  return [activeKeys, onChange]
}
